import { flexRender, type RowData } from "@tanstack/react-table";
import { useMemo } from "react";
import { Link, useLocation } from "react-router";

import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { HistogramChart } from "@/components/Charts/HistogramChart";
import { dataColors } from "@/components/Charts/theme";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Pagination } from "@/components/Pagination/Pagination";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Stat } from "@/components/Stat/Stat";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { SectionCard } from "@/design-system/SectionCard";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import type { DashboardTable } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { formatSignedChange } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { AnalyticsPageContent } from "@/modules/analytics/components/AnalyticsPageContent";
import { AnalyticsSection } from "@/modules/analytics/components/AnalyticsSection";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { LineChart } from "@/modules/analytics/components/LineChart";
import { ANALYTICS_TABLE_PAGE_SIZE } from "@/modules/analytics/constants";
import type {
  TicketAnalyticsCategory,
  TicketAnalyticsDayBucket,
  TicketAnalyticsResponse,
  TicketAnalyticsRow,
  TicketAnalyticsVisibility,
} from "@/queries/analytics/ticket-reporting-types";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import type { TicketAnalyticsChartFilter, TicketAnalyticsDateRangeOption } from "./TicketAnalyticsPage";

const TICKET_DAY_BUCKETS = [
  "lessThan1Day",
  "1To3Days",
  "4To7Days",
  "8To14Days",
  "15DaysOrMore",
] satisfies readonly TicketAnalyticsDayBucket[];
const TICKET_VISIBILITIES = ["private", "collective"] satisfies readonly Exclude<TicketAnalyticsVisibility, "all">[];

interface Props {
  /** `focused` shows the core story without table filters; `all` enables every section and interaction. */
  mode?: "focused" | "all";
  data: TicketAnalyticsResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  selectedDateRange: TicketAnalyticsDateRangeOption;
  dateRangeOptions: NonEmptyArray<TicketAnalyticsDateRangeOption>;
  onDateRangeChange: (option: TicketAnalyticsDateRangeOption) => void;
  search: string;
  onSearchChange: (search: string) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
  ticketTable: DashboardTable<TicketAnalyticsRow>;
  categoryTable: DashboardTable<TicketAnalyticsCategory>;
  chartFilter: TicketAnalyticsChartFilter;
  onChartFilter: (filter: NonNullable<TicketAnalyticsChartFilter>) => void;
  onClearChartFilter: () => void;
  page: number;
  pageCount: number;
  resultCount: number;
  onPageChange: (page: number) => void;
}

/** Renders ticket reporting controls, charts, and the filtered ticket table. */
export function TicketAnalyticsContent({
  mode = "all",
  data,
  isLoading,
  isFetching,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
  search,
  onSearchChange,
  activeFilterCount,
  onOpenFilters,
  ticketTable,
  categoryTable,
  chartFilter,
  onChartFilter,
  onClearChartFilter,
  page,
  pageCount,
  resultCount,
  onPageChange,
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const analyticsScope = useAnalyticsScope();
  const location = useLocation();
  const slug = useSlug({ optional: true });
  const projectId = useProjectId();
  const portfolioRoute = (path: string) => addPortfolioProjectFilterQueryParams(path, location.search);
  const combinedFilterCount =
    activeFilterCount + (analyticsScope.type === "portfolio" ? analyticsScope.projectFilter.activeCount : 0);

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={mode === "focused" ? t("page.analytics-tickets.focus.title") : t("page.analytics-tickets.title")}
      title={mode === "focused" ? t("page.analytics-tickets.focus.title") : t("page.analytics-tickets.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            analyticsScope.type === "portfolio"
              ? { name: t("page.portfolio.title"), to: portfolioRoute(routes.portfolio.overview()) }
              : { name: t("page.analytics-story.title"), to: routes.analytics.overview({ slug: slug! }) },
            { name: t("page.analytics-tickets.breadcrumb") },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [projectId] }}
          context={{ scope: analyticsScope.type, entity: "analytics", page: "detail" }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.analytics.suggestion.1"),
            t("component.ask-nobi-chat-drawer.analytics.suggestion.2"),
          ]}
        />
      }
      header={
        <Inline gap="md" align="center">
          <Inline gap="sm" align="center" wrap={false}>
            <span className="text-body-emphasis">{t("page.analytics-details.time-range.label")}</span>
            <Select
              items={dateRangeOptions}
              selected={selectedDateRange}
              onChange={onDateRangeChange}
              renderOption={(option) => option.name}
              keySelector={(option) => option.value}
            />
          </Inline>
          <Button variant="soft" icon={<FilterFunnel01Icon />} onClick={onOpenFilters}>
            <Inline as="span" gap="sm" align="center" wrap={false}>
              {t("common.action.filter")}
              {combinedFilterCount > 0 && <NumericBadge count={combinedFilterCount} variant="filled" />}
            </Inline>
          </Button>
          {isFetching && !isLoading && <Spinner size="sm" />}
        </Inline>
      }
    >
      <AnalyticsPageContent>
        {mode === "focused" && (
          <p className="max-w-measure-wide text-body text-text-secondary">
            {t("page.analytics-tickets.focus.description")}
          </p>
        )}
        {isLoading || !data ? (
          <section className="flex flex-1 items-center justify-center py-space-xl" aria-busy="true">
            <Spinner size="xl" />
          </section>
        ) : (
          <Content
            mode={mode}
            data={data}
            search={search}
            onSearchChange={onSearchChange}
            onOpenFilters={onOpenFilters}
            activeFilterCount={combinedFilterCount}
            ticketTable={ticketTable}
            categoryTable={categoryTable}
            chartFilter={chartFilter}
            onChartFilter={onChartFilter}
            onClearChartFilter={onClearChartFilter}
            page={page}
            pageCount={pageCount}
            resultCount={resultCount}
            onPageChange={onPageChange}
          />
        )}
      </AnalyticsPageContent>
    </DocumentPage>
  );
}

function Content({
  mode,
  data,
  search,
  onSearchChange,
  onOpenFilters,
  activeFilterCount,
  ticketTable,
  categoryTable,
  chartFilter,
  onChartFilter,
  onClearChartFilter,
  page,
  pageCount,
  resultCount,
  onPageChange,
}: Pick<
  Props,
  | "mode"
  | "search"
  | "onSearchChange"
  | "onOpenFilters"
  | "activeFilterCount"
  | "ticketTable"
  | "categoryTable"
  | "chartFilter"
  | "onChartFilter"
  | "onClearChartFilter"
  | "page"
  | "pageCount"
  | "resultCount"
  | "onPageChange"
> & {
  data: TicketAnalyticsResponse;
}): React.ReactNode {
  const { i18n, t } = useTranslation();
  const help = useMetricHelp();
  const analyticsScope = useAnalyticsScope();
  const isPortfolio = analyticsScope.type === "portfolio";
  const slug = useSlug({ optional: true });
  const { summary } = data;

  const createdData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.created,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const closedData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.closed,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const backlogData = useMemo(
    () => [
      {
        label: t("page.analytics-tickets.activity.backlog"),
        color: dataColors.orange,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.openAtEnd,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );
  const resolutionData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.averageResolutionDays,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const resolutionBenchmark = useMemo(
    () => data.benchmark.map((point) => ({ x: point.x, y: point.averageResolutionDays })),
    [data.benchmark],
  );
  const satisfactionData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.averageRating,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const satisfactionBenchmark = useMemo(
    () => data.benchmark.map((point) => ({ x: point.x, y: point.averageRating })),
    [data.benchmark],
  );
  const feedbackCoverageData = useMemo(
    () => [
      {
        category: t("page.analytics-tickets.resident-impact.rated"),
        value: summary.ratedTickets,
      },
      {
        category: t("page.analytics-tickets.resident-impact.not-rated"),
        value: Math.max(0, summary.createdTickets - summary.ratedTickets),
      },
    ],
    [summary.createdTickets, summary.ratedTickets, t],
  );
  const backlogAgeData = useMemo(
    () =>
      data.backlogAgeBuckets.flatMap((point) =>
        isTicketDayBucket(point.x)
          ? [
              {
                category: point.x,
                label: t(`page.analytics-tickets.day-bucket.${point.x}`),
                value: point.y,
              },
            ]
          : [],
      ),
    [data.backlogAgeBuckets, t],
  );
  const resolutionBucketData = useMemo(
    () =>
      data.resolutionBuckets.flatMap((point) =>
        isTicketDayBucket(point.x)
          ? [
              {
                category: point.x,
                label: t(`page.analytics-tickets.day-bucket.${point.x}`),
                value: point.y,
              },
            ]
          : [],
      ),
    [data.resolutionBuckets, t],
  );
  const visibilityData = useMemo(
    () =>
      data.visibilityDistribution.flatMap((point) =>
        isTicketVisibility(point.x)
          ? [
              {
                category: point.x,
                label: t(`page.analytics-tickets.filters.visibility.${point.x}`),
                value: point.y,
              },
            ]
          : [],
      ),
    [data.visibilityDistribution, t],
  );

  const rangeFrom = resultCount === 0 ? 0 : page * ANALYTICS_TABLE_PAGE_SIZE + 1;
  const rangeTo = Math.min((page + 1) * ANALYTICS_TABLE_PAGE_SIZE, resultCount);

  return (
    <PageGrid.Row>
      <PageGrid.Column size="100%">
        <AnalyticsSection
          isFullWidth
          title={
            mode === "focused"
              ? t("page.analytics-tickets.focus.signals")
              : t("page.analytics-tickets.overview.section-title")
          }
        >
          <AnalyticsCard.Root>
            <AnalyticsCard.Header
              title={t("page.analytics-tickets.overview.title")}
              tooltip={help("page.analytics-tickets.overview.title.tooltip")}
            />
            <section className="flex flex-col gap-space-md @xs:flex-row @xs:flex-wrap">
              {mode === "all" && (
                <Stat
                  label={t("page.analytics-tickets.overview.created")}
                  value={summary.createdTickets}
                  renderFooter={() =>
                    t("page.analytics-tickets.overview.previous-period-change", {
                      change: formatSignedChange(
                        summary.createdTickets - summary.previousPeriod.createdTickets,
                        i18n.language,
                      ),
                    })
                  }
                />
              )}
              {mode === "all" && (
                <Stat
                  label={t("page.analytics-tickets.overview.closed")}
                  value={summary.closedTickets}
                  renderFooter={() =>
                    t("page.analytics-tickets.overview.previous-period-change", {
                      change: formatSignedChange(
                        summary.closedTickets - summary.previousPeriod.closedTickets,
                        i18n.language,
                      ),
                    })
                  }
                />
              )}
              <Stat
                label={t("page.analytics-tickets.overview.open")}
                value={summary.openTickets}
                renderFooter={() =>
                  t("page.analytics-tickets.overview.selected-period-change", {
                    change: formatSignedChange(summary.openTicketsChange, i18n.language),
                  })
                }
              />
              {mode === "all" && (
                <Stat
                  label={t("page.analytics-tickets.overview.reporters")}
                  value={summary.uniqueReporters}
                  renderFooter={() =>
                    t("page.analytics-tickets.overview.resident-reach", {
                      percentage: summary.reporterReachPercentage,
                      total: summary.eligibleResidents,
                      change: formatSignedChange(
                        summary.uniqueReporters - summary.previousPeriod.uniqueReporters,
                        i18n.language,
                      ),
                    })
                  }
                />
              )}
              <Stat
                label={t("page.analytics-tickets.overview.resolution-time")}
                value={summary.medianResolutionDays}
                renderValue={(value) =>
                  value === undefined ? (
                    "-"
                  ) : (
                    <span className="whitespace-nowrap">
                      {value}
                      <span className="text-heading-2"> {t("page.analytics-tickets.value.days-unit")}</span>
                    </span>
                  )
                }
                renderFooter={() => {
                  if (summary.p90ResolutionDays === undefined) {
                    return undefined;
                  }

                  if (
                    summary.medianResolutionDays === undefined ||
                    summary.previousPeriod.medianResolutionDays === undefined
                  ) {
                    return t("page.analytics-tickets.overview.p90-resolution-value", {
                      value: summary.p90ResolutionDays,
                    });
                  }

                  return t("page.analytics-tickets.overview.p90-resolution", {
                    value: summary.p90ResolutionDays,
                    change: Math.abs(
                      Math.round((summary.medianResolutionDays - summary.previousPeriod.medianResolutionDays) * 10) /
                        10,
                    ),
                    direction:
                      summary.medianResolutionDays <= summary.previousPeriod.medianResolutionDays
                        ? t("page.analytics-tickets.overview.faster")
                        : t("page.analytics-tickets.overview.slower"),
                  });
                }}
              />
              <Stat
                label={t("page.analytics-tickets.overview.satisfaction")}
                value={summary.averageRating}
                renderValue={(value) =>
                  value === undefined ? (
                    "-"
                  ) : (
                    <span className="whitespace-nowrap">
                      {value}
                      <span className="text-heading-2">/10</span>
                    </span>
                  )
                }
                renderFooter={() =>
                  t("page.analytics-tickets.overview.rated-tickets", {
                    count: summary.ratedTickets,
                    percentage:
                      summary.createdTickets === 0
                        ? 0
                        : Math.round((100 * summary.ratedTickets) / summary.createdTickets),
                    change:
                      summary.averageRating === undefined || summary.previousPeriod.averageRating === undefined
                        ? "-"
                        : formatSignedChange(
                            summary.averageRating - summary.previousPeriod.averageRating,
                            i18n.language,
                          ),
                  })
                }
              />
            </section>
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      {mode === "all" && (
        <PageGrid.Column size="100%">
          <AnalyticsSection title={t("page.analytics-tickets.activity.section-title")}>
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-tickets.activity.created-closed")}
                tooltip={help("page.analytics-tickets.activity.created-closed.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                <BarChart
                  dataPrimary={{
                    data: createdData,
                    onClick: (point) =>
                      onChartFilter({
                        type: "month",
                        value: String(point.x),
                        label: formatDate(i18n, "monthYear", point.x),
                      }),
                  }}
                  dataSecondary={{
                    data: closedData,
                    color: dataColors.green,
                    onClick: (point) =>
                      onChartFilter({
                        type: "month",
                        value: String(point.x),
                        label: formatDate(i18n, "monthYear", point.x),
                      }),
                  }}
                  xAxisType="date"
                  yLabel={t("page.analytics-tickets.axis.tickets")}
                  yTickMode="integer"
                  hoverLabel={(bar) => (
                    <Stack gap="xs">
                      <span>
                        {t("page.analytics-tickets.overview.created")}: {bar.primary?.y ?? 0}
                      </span>
                      <span>
                        {t("page.analytics-tickets.overview.closed")}: {bar.secondary?.y ?? 0}
                      </span>
                      {(bar.primary?.isPending || bar.secondary?.isPending) && (
                        <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                      )}
                    </Stack>
                  )}
                />
                <ChartLegend
                  items={[
                    { label: t("page.analytics-tickets.overview.created"), color: dataColors.primary },
                    { label: t("page.analytics-tickets.overview.closed"), color: dataColors.green },
                    {
                      label: t("page.analytics-details.engagement.chart.legend.current-period"),
                      color: dataColors.primary,
                      pattern: "striped",
                    },
                  ]}
                />
              </figure>
            </AnalyticsCard.Root>
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-tickets.activity.backlog")}
                tooltip={help("page.analytics-tickets.activity.backlog.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                <LineChart
                  data={backlogData}
                  xAxisType="date"
                  yLabel={t("page.analytics-tickets.axis.open-tickets")}
                  hoverLabel={(point) => (
                    <Stack gap="xs">
                      <span>{point.relatedPoints[0].dataPoint.y}</span>
                      {point.relatedPoints[0].dataPoint.isPending && (
                        <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                      )}
                    </Stack>
                  )}
                />
                <ChartLegend items={[{ label: backlogData[0].label, color: dataColors.orange }]} />
              </figure>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      <PageGrid.Column size="100%">
        <AnalyticsSection
          title={t("page.analytics-tickets.backlog-health.section-title")}
          description={t("page.analytics-tickets.backlog-health.description")}
        >
          <AnalyticsCard.Root height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-tickets.backlog-health.open-age")}
              tooltip={help("page.analytics-tickets.backlog-health.open-age.tooltip")}
            />
            <HistogramChart
              data={backlogAgeData}
              onClickBar={
                mode === "all"
                  ? (point) =>
                      onChartFilter({
                        type: "backlogAge",
                        value: point.category,
                        label: point.label ?? point.category,
                      })
                  : undefined
              }
            />
          </AnalyticsCard.Root>
          <AnalyticsCard.Root height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-tickets.backlog-health.closed-resolution")}
              tooltip={help("page.analytics-tickets.backlog-health.closed-resolution.tooltip")}
            />
            <HistogramChart
              data={resolutionBucketData}
              onClickBar={
                mode === "all"
                  ? (point) =>
                      onChartFilter({
                        type: "resolution",
                        value: point.category,
                        label: point.label ?? point.category,
                      })
                  : undefined
              }
            />
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      <PageGrid.Column size="100%">
        <AnalyticsSection
          title={t("page.analytics-tickets.service.section-title")}
          description={t("page.analytics-tickets.service.description")}
        >
          <AnalyticsCard.Root variant="graph" height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-tickets.service.resolution-time")}
              tooltip={help("page.analytics-tickets.service.resolution-time.tooltip")}
            />
            <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
              <BarChart
                dataPrimary={{ data: resolutionData }}
                dataBenchmark={resolutionBenchmark}
                xAxisType="date"
                yLabel={t("page.analytics-tickets.axis.days")}
                formatYTick={(value) => `${value}d`}
                hoverLabel={(bar) => (
                  <Stack gap="xs">
                    <span>
                      {t("page.analytics-tickets.service.project")}: {bar.primary?.y ?? "-"}d
                    </span>
                    <span>
                      {t("common.label.aop-benchmark")}: {bar.benchmark?.y ?? "-"}d
                    </span>
                    {bar.primary?.isPending && (
                      <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                    )}
                  </Stack>
                )}
              />
              <ChartLegend
                items={[
                  { label: t("page.analytics-tickets.service.project"), color: dataColors.primary },
                  { label: t("common.label.aop-benchmark"), color: dataColors.benchmark },
                ]}
              />
            </figure>
          </AnalyticsCard.Root>
          <AnalyticsCard.Root variant="graph" height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-tickets.service.satisfaction")}
              tooltip={help("page.analytics-tickets.service.satisfaction.tooltip")}
            />
            <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
              <BarChart
                dataPrimary={{ data: satisfactionData, color: dataColors.green }}
                dataBenchmark={satisfactionBenchmark}
                xAxisType="date"
                yLabel={t("page.analytics-tickets.axis.rating")}
                maxY={10}
                formatYTick={(value) => `${value}`}
                hoverLabel={(bar) => (
                  <Stack gap="xs">
                    <span>
                      {t("page.analytics-tickets.service.project")}: {bar.primary?.y ?? "-"}/10
                    </span>
                    <span>
                      {t("common.label.aop-benchmark")}: {bar.benchmark?.y ?? "-"}/10
                    </span>
                    {bar.primary?.isPending && (
                      <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                    )}
                  </Stack>
                )}
              />
              <ChartLegend
                items={[
                  { label: t("page.analytics-tickets.service.project"), color: dataColors.green },
                  { label: t("common.label.aop-benchmark"), color: dataColors.benchmark },
                ]}
              />
            </figure>
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      {mode === "all" && (
        <PageGrid.Column size="100%">
          <AnalyticsSection
            title={t("page.analytics-tickets.resident-impact.section-title")}
            description={t("page.analytics-tickets.resident-impact.description")}
          >
            <AnalyticsCard.Root height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-tickets.resident-impact.feedback-coverage")}
                tooltip={help("page.analytics-tickets.resident-impact.feedback-coverage.tooltip")}
              />
              <HistogramChart data={feedbackCoverageData} />
            </AnalyticsCard.Root>
            <AnalyticsCard.Root height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-tickets.resident-impact.visibility")}
                tooltip={help("page.analytics-tickets.resident-impact.visibility.tooltip")}
              />
              <HistogramChart data={visibilityData} />
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      <PageGrid.Column size="100%">
        <AnalyticsSection
          isFullWidth
          title={t("page.analytics-tickets.category-performance.section-title")}
          description={t("page.analytics-tickets.category-performance.description")}
        >
          <AnalyticsCard.Root horizontalScroll>
            <AnalyticsCard.Header title={t("page.analytics-tickets.category-performance.title")} />
            <figure className="invisible hidden @sm:visible @sm:block">
              <Table table={categoryTable} data-testid="analytics-ticket-categories-table" />
            </figure>
            <MobileTableCards table={categoryTable} dataTestId="analytics-ticket-categories-mobile-list" />
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      {mode === "all" && !isPortfolio && (
        <PageGrid.Column size="100%">
          <AnalyticsSection isFullWidth title={t("page.analytics-tickets.table.section-title")}>
            <AnalyticsCard.Root horizontalScroll>
              <AnalyticsCard.Header title={t("page.analytics-tickets.table.title")}>
                <div className="flex w-full flex-col gap-space-md @sm:w-auto @sm:flex-row @sm:items-center">
                  {chartFilter && (
                    <Inline gap="sm" align="center" wrap={false}>
                      <span className="text-caption text-text-secondary">
                        {t("page.analytics-tickets.table.chart-filter", { value: chartFilter.label })}
                      </span>
                      <Button variant="subtle" size="sm" onClick={onClearChartFilter}>
                        {t("common.action.clear")}
                      </Button>
                    </Inline>
                  )}
                  <SearchInput
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder={t("page.analytics-tickets.table.search")}
                    aria-label={t("page.analytics-tickets.table.search")}
                    width="compact"
                  />
                  <Button variant="soft" icon={<FilterFunnel01Icon />} onClick={onOpenFilters}>
                    <Inline gap="sm" align="center" wrap={false}>
                      {t("common.action.filter")}
                      {activeFilterCount > 0 && <NumericBadge count={activeFilterCount} variant="filled" />}
                    </Inline>
                  </Button>
                </div>
              </AnalyticsCard.Header>
              <figure className="invisible hidden @sm:visible @sm:block">
                <Table
                  table={ticketTable}
                  data-testid="analytics-tickets-table"
                  getRowHref={(row) => routes.tickets.details({ slug: slug!, id: row.original.id })}
                >
                  {resultCount > 0 && (
                    <TableFooter
                      from={rangeFrom}
                      to={rangeTo}
                      total={resultCount}
                      page={page}
                      pageCount={pageCount}
                      onPageChange={onPageChange}
                    />
                  )}
                </Table>
              </figure>
              <MobileTableCards
                table={ticketTable}
                dataTestId="analytics-tickets-mobile-list"
                getRowHref={(row) => routes.tickets.details({ slug: slug!, id: row.id })}
              />
              {resultCount > 0 && (
                <footer className="mt-space-md @sm:hidden">
                  <TableFooter
                    from={rangeFrom}
                    to={rangeTo}
                    total={resultCount}
                    page={page}
                    pageCount={pageCount}
                    onPageChange={onPageChange}
                  />
                </footer>
              )}
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}
    </PageGrid.Row>
  );
}

function MobileTableCards<TData extends RowData>({
  table,
  dataTestId,
  getRowHref,
}: {
  table: DashboardTable<TData>;
  dataTestId: string;
  getRowHref?: (row: TData) => string;
}): React.ReactNode {
  return (
    <section className="flex flex-col gap-space-md @sm:hidden" data-testid={dataTestId}>
      {table.getRowModel().rows.map((row) => {
        const content = (
          <dl className="flex flex-col gap-space-md">
            {row.getVisibleCells().map((cell) => {
              const header = cell.column.columnDef.header;

              return (
                <Spread key={cell.id} space="md" align="start">
                  <dt className="text-caption text-text-secondary">
                    {typeof header === "string" ? header : cell.column.id}
                  </dt>
                  <dd className="min-w-0 text-right text-caption-emphasis">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </dd>
                </Spread>
              );
            })}
          </dl>
        );
        const href = getRowHref?.(row.original);

        return href ? (
          <Link key={row.id} className="text-text-primary no-underline" to={href}>
            <SectionCard>{content}</SectionCard>
          </Link>
        ) : (
          <SectionCard key={row.id}>{content}</SectionCard>
        );
      })}
    </section>
  );
}

function TableFooter({
  from,
  to,
  total,
  page,
  pageCount,
  onPageChange,
}: {
  from: number;
  to: number;
  total: number;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Inline gap="md" align="center" justify="between">
      <span className="text-caption text-text-secondary">
        {t("page.analytics-tickets.table.results", { from, to, total })}
      </span>
      {pageCount > 1 && <Pagination count={pageCount} currentIndex={page} onChange={onPageChange} />}
    </Inline>
  );
}

function isTicketDayBucket(value: string): value is TicketAnalyticsDayBucket {
  return TICKET_DAY_BUCKETS.some((bucket) => bucket === value);
}

function isTicketVisibility(value: string): value is Exclude<TicketAnalyticsVisibility, "all"> {
  return TICKET_VISIBILITIES.some((visibility) => visibility === value);
}
